import { isEmpty } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LuChevronDown, LuChevronUp, LuListFilter } from 'react-icons/lu'
import { useTheme } from 'styled-components'

import { FeatureFlag, WhenFlag } from '@/feature/feature-flags'
import { TableHeaderTags, TagsModalContainer, TagsPopout } from '@/feature/tags'
import { TagsFilterPopout } from '@/feature/tags/components/TagsFilterPopout'

import { Checkmark } from '@/lib/core/components/Checkmark'
import { Loader } from '@/lib/core/components/Loader'
import { TableSearch } from '@/lib/core/components/Table/components'
import {
  CheckmarkTh,
  CounterWrapper,
  FullWidthCell,
  HeaderActions,
  LoadingWrapper,
  ResizeHandle,
  StartRow,
  StickyLoader,
  StyledTable,
  TableContent,
  TableHeader,
  TableHeaderLeft,
  TableHeaderRight,
  TableHeaderTitle,
  Th,
  Tr,
  Wrapper,
} from '@/lib/core/components/Table/Table.styles'
import { SortDirection } from '@/lib/core/interfaces/common.types'

import { TableProps } from './Table.types'

export const sortRotation: { [key: string]: SortDirection | undefined } = {
  [SortDirection.ASC]: SortDirection.DESC,
  [SortDirection.DESC]: undefined,
  undefined: SortDirection.ASC,
}

export const Table = ({
  checkAll,
  children,
  columns,
  customNoResultsText,
  elementsCounter,
  externalAllChecked,
  resizableTable = false,
  header,
  hideHeader,
  isLoading,
  isSelectable,
  onSearch,
  searchFilter,
  onSort,
  sortInfo = {},
  stickyLoader = true,
  renderCustomFilter,
  dataTestId,
  shouldRenderTagsFilterPopout = false,
}: TableProps) => {
  const { colors } = useTheme()
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const resizingRef = useRef<{ accessor: string; startX: number; startWidth: number } | null>(null)
  const didResizeRef = useRef(false)
  const hasActions = !!onSearch

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, accessor: string, thElement: HTMLTableCellElement) => {
      e.preventDefault()
      e.stopPropagation()
      const startWidth = thElement.offsetWidth
      resizingRef.current = { accessor, startX: e.clientX, startWidth }
      didResizeRef.current = false

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!resizingRef.current) return
        const delta = moveEvent.clientX - resizingRef.current.startX
        if (Math.abs(delta) > 2) {
          didResizeRef.current = true
        }
        const newWidth = Math.max(50, resizingRef.current.startWidth + delta)
        setColumnWidths((prev) => ({ ...prev, [accessor]: newWidth }))
      }

      const handleMouseUp = () => {
        resizingRef.current = null
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        requestAnimationFrame(() => {
          didResizeRef.current = false
        })
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [],
  )

  const onCheckAll = () => {
    const newValue = !isAllChecked

    setIsAllChecked(newValue)

    if (checkAll) checkAll(newValue)
  }

  useEffect(() => {
    setIsAllChecked(!!externalAllChecked)
  }, [externalAllChecked])

  return (
    <>
      {stickyLoader && isLoading && (
        <StickyLoader>
          <Loader color={colors.backgroundTab} />
        </StickyLoader>
      )}
      <Wrapper data-testid={dataTestId}>
        {!hideHeader && (
          <TableHeader>
            <TableHeaderLeft>
              {(header && <TableHeaderTitle>{header}</TableHeaderTitle>) ||
                renderCustomFilter ||
                (typeof elementsCounter === 'number' && (
                  <CounterWrapper>All ({elementsCounter})</CounterWrapper>
                ))}
              <FeatureFlag flag="assetTags">
                <WhenFlag>
                  <TableHeaderTags shouldDisplay={shouldRenderTagsFilterPopout} />
                  <TagsPopout />
                  <TagsFilterPopout />
                  <TagsModalContainer />
                </WhenFlag>
              </FeatureFlag>
            </TableHeaderLeft>
            <TableHeaderRight>
              {hasActions && (
                <HeaderActions>
                  {onSearch && <TableSearch defaultValue={searchFilter} onSearch={onSearch} />}
                </HeaderActions>
              )}
            </TableHeaderRight>
          </TableHeader>
        )}
        <TableContent>
          {isLoading && (
            <LoadingWrapper>
              {!stickyLoader && <Loader color={colors.backgroundTab} />}
            </LoadingWrapper>
          )}
          <StyledTable
            $isLoading={isLoading}
            $isSelectable={isSelectable}
            $resizableTable={resizableTable}
            cellSpacing="0"
          >
            <thead>
              <tr>
                {isSelectable && (
                  <CheckmarkTh>
                    <Checkmark
                      isActive={
                        typeof externalAllChecked === 'boolean' ? externalAllChecked : isAllChecked
                      }
                      onClick={onCheckAll}
                    />
                  </CheckmarkTh>
                )}
                {columns.map(
                  ({
                    accessor,
                    sortKey,
                    label,
                    width,
                    textAlign,
                    sortable,
                    tooltip,
                    resizableColumn,
                  }) => (
                    <Th
                      key={`${accessor}-${label}`}
                      $resizable={resizableColumn}
                      // For some reason typescript is being a little inconsistent with whether this is an error ot not
                      // eslint-disable-next-line
                      // @ts-ignore Typing error in styled components it seems
                      css={{
                        textAlign,
                        width: columnWidths[accessor] ? `${columnWidths[accessor]}px` : width,
                        cursor: sortable ? 'pointer' : 'auto',
                      }}
                      onClick={() => {
                        if (didResizeRef.current) return
                        if (sortable && onSort) {
                          onSort(
                            sortKey ?? accessor,
                            sortRotation[`${sortInfo[sortKey ?? accessor]}`],
                          )
                        }
                      }}
                    >
                      <StartRow>
                        {label}

                        {tooltip}

                        {!sortable
                          ? null
                          : {
                              [SortDirection.ASC]: <LuChevronDown size="16" />,
                              [SortDirection.DESC]: <LuChevronUp size="16" />,
                              undefined: <LuListFilter size="16" />,
                            }[sortInfo[sortKey ?? accessor] as string]}
                      </StartRow>
                      {resizableColumn && (
                        <ResizeHandle
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => {
                            const th = e.currentTarget.parentElement as HTMLTableCellElement
                            handleMouseDown(e, accessor, th)
                          }}
                        />
                      )}
                    </Th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {!isEmpty(children) ? (
                children
              ) : (
                <Tr>
                  <FullWidthCell colSpan={columns.length + (isSelectable ? 1 : 0)}>
                    {customNoResultsText ?? 'No results.'}
                  </FullWidthCell>
                </Tr>
              )}
            </tbody>
          </StyledTable>
        </TableContent>
      </Wrapper>
    </>
  )
}
